import { Box, Typography } from '@mui/material';
import TeamSummaryCard from '../../../components/reporting/TeamSummaryCard';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';

export default function ByTeamReport(props) {
  const { selectedTeam, adjustedTeamsMap, teams, eventsScores, eventHasAdjustments, teamModes, setTeamModes, setAutoAppliedModes, categoriesMap, divisionsMap, setManualOverrides } = props;
  if (!selectedTeam) return <Typography>Please select a team</Typography>;

  const teamEntry = adjustedTeamsMap?.[selectedTeam] || {};
  const teamInfo = teams?.[selectedTeam] || {};
  const currentMode = teamModes[selectedTeam] || 'normal';

  const showAdjusted = (props.affiliatedMode && props.affiliatedMode !== 'none') || Object.values(teamModes || {}).some(m => m !== 'normal');

  const rows = Object.keys(eventsScores).map((e) => {
    const event = eventsScores[e];
    const targetScore = (event.scores || []).find(x => x.teamUid === selectedTeam);
    if (!targetScore) return null;
    return {
      id: `${e}-${selectedTeam}`,
      eventNumber: event.event.number,
      eventName: `${event.event.name} ${event.event.company}`,
      affiliated: (event.event?.associatedTeams || []).map(a => (typeof a === 'string' ? a : a.uid)).includes(selectedTeam),
      finalScoreRaw: targetScore.finalScore,
      finalScoreAdjusted: targetScore.finalScoreAssociated ?? targetScore.finalScore,
      categoryAvg: event.categoriesAvg?.[targetScore.team.category] ?? '',
      overallAvg: event.avgOverall ?? '',
      categoryTop: event.categoryTop?.[targetScore.team.category] ?? '',
      overallTop: event.overallTopInEvent ?? '',
      categoryPlace: targetScore.categoryPlace,
      divisionPlace: targetScore.divisionPlace,
      overallPlace: targetScore.overallPlace,
      overallPlaceAssociated: targetScore.overallPlaceAssociated,
      divisionPlaceAssociated: targetScore.divisionPlaceAssociated
    };
  }).filter(x => x !== null);

  const columns = [
    { field: 'eventNumber', headerName: 'Event Number', width: 140 },
    { field: 'eventName', headerName: 'Event Name', width: 300 },
    { field: 'affiliated', headerName: 'Affiliated', width: 120, renderCell: (params) => {
      const isAff = params.value;
      return isAff ? <CheckCircleIcon sx={{ color: 'green' }} /> : <CancelIcon sx={{ color: 'red' }} />
    } },
    { field: 'finalScoreRaw', headerName: 'Final Score (Raw)', width: 140 },
  ];

  if (showAdjusted) {
    columns.push({ field: 'finalScoreAdjusted', headerName: 'Final Score (Adjusted)', width: 160 });
  }

  columns.push({ field: 'categoryPlace', headerName: 'Category Place', width: 140 });
  columns.push({ field: 'overallPlace', headerName: 'Original Place', width: 140 });
  if (showAdjusted) {
    columns.push({ field: 'overallPlaceAssociated', headerName: 'Adjusted Place', width: 140 });
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Box sx={{ flex: 0.75 }}>
          <TeamSummaryCard
            teamUid={selectedTeam}
            teamEntry={teamEntry}
            teamInfo={teamInfo}
            categoriesMap={categoriesMap}
            divisionsMap={divisionsMap}
            eventsScores={eventsScores}
            teamModes={teamModes}
            setTeamModes={setTeamModes}
            setAutoAppliedModes={setAutoAppliedModes}
            setManualOverrides={setManualOverrides}
            affiliatedMode={props.affiliatedMode}
          />
        </Box>
      </Box>

      <DataGrid
        rows={rows}
        columns={columns}
        slots={{ toolbar: GridToolbar }}
        pageSizeOptions={[25, 50, 100]}
        initialState={{ pagination: { paginationModel: { pageSize: 25 } }, sorting: { sortModel: [{ field: (showAdjusted ? 'overallPlaceAssociated' : 'overallPlace'), sort: 'asc' }] } }}
      />
    </Box>
  );
}
